import { Controller } from "@hotwired/stimulus"

// Onboarding step 1: turns a typed brand name into spelling suggestions the user
// picks from as chips. Nothing is selected for them — a term silently added to
// the alert is a term they never chose to be matched on.
export default class extends Controller {
  static targets = ["brand", "chips", "customRow", "customInput", "preview", "termsInput"]

  #selected = new Map()

  connect() {
    this.refresh()
  }

  onBrandInput() {
    this.refresh()
  }

  toggle(e) {
    const term = e.currentTarget.dataset.term
    if (this.#selected.has(term)) {
      this.#selected.delete(term)
    } else {
      this.#selected.set(term, e.currentTarget.dataset.reason)
    }
    this.refresh()
  }

  showCustom() {
    this.customRowTarget.classList.remove("hidden")
    this.customInputTarget.focus()
  }

  addCustom(e) {
    if (e?.type === "keydown") e.preventDefault()
    const value = this.customInputTarget.value.trim()
    if (!value) return
    this.#selected.set(value.toLowerCase(), "added")
    this.customInputTarget.value = ""
    this.refresh()
  }

  refresh() {
    this.#renderChips()
    this.#syncHidden()
  }

  #derive() {
    const brand = this.#brand()
    if (!brand) return []
    const low = brand.toLowerCase()
    const out = []
    const push = (v, reason) => {
      const lv = v.toLowerCase()
      if (lv && lv !== low && !out.some(o => o.term === lv)) out.push({ term: lv, reason })
    }
    const spaced = brand.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    if (spaced.toLowerCase() !== low) push(spaced.toLowerCase(), "with a space")
    if (/\s/.test(brand)) push(brand.replace(/\s+/g, "").toLowerCase(), "no space")
    if (spaced.toLowerCase() !== low) push(spaced.toLowerCase().replace(/\s+/g, "-"), "with a hyphen")
    push(low + "'s", "possessive")
    return out.slice(0, 4)
  }

  #renderChips() {
    const rows = this.#derive()
    for (const [term, reason] of this.#selected) {
      if (!rows.some(r => r.term === term)) rows.push({ term, reason })
    }

    const chips = rows.map(row => {
      const on = this.#selected.has(row.term)
      const shell = on
        ? "border-brand bg-brand/[.06] text-ink"
        : "border-line-strong bg-white text-ink-slate hover:border-brand"
      const check = on ? '<i class="bi bi-check-lg text-brand text-[13px]"></i>' : ""
      return `
        <button type="button" data-term="${row.term}" data-reason="${row.reason}"
                data-action="brand-variations#toggle" title="${row.reason}"
                class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[14px] font-semibold cursor-pointer transition-colors ${shell}">
          ${check}${row.term}
        </button>`
    })

    chips.push(`
      <button type="button" data-action="brand-variations#showCustom"
              class="inline-flex items-center gap-1.5 rounded-full border border-dashed border-line-strong bg-white px-3 py-1.5 text-[14px] font-semibold text-brand cursor-pointer hover:border-brand">
        <i class="bi bi-plus-lg text-[12px]"></i>Add your own
      </button>`)

    this.chipsTarget.innerHTML = chips.join("")
  }

  #syncHidden() {
    const brand = this.#brand()
    const terms = []
    if (brand) terms.push(brand)
    for (const term of this.#selected.keys()) {
      if (!terms.some(t => t.toLowerCase() === term)) terms.push(term)
    }
    this.termsInputTarget.value = terms.join("\n")

    const extras = terms.length - 1
    this.previewTarget.textContent = brand
      ? (extras > 0
          ? `Watching “${brand}” and ${extras} spelling${extras > 1 ? "s" : ""}.`
          : `Watching “${brand}”.`)
      : ""
  }

  #brand() {
    return (this.hasBrandTarget ? this.brandTarget.value : "").trim()
  }
}
